import { useState, type JSX } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../ui/common.js';
import { api } from '../api/client.js';
import { kstHHmm } from '../lib/format.js';

/** ISO(UTC) → time input 용 'HH:mm'(KST). 없으면 ''. */
function hhmm(iso?: string | null): string {
  return iso ? kstHHmm(iso) : '';
}
/** 'HH:mm'(KST) + 'YYYY-MM-DD' → UTC ISO. 빈 값이면 null. */
function toIso(date: string, time: string): string | null {
  if (!time) return null;
  const d = new Date(`${date}T${time}:00+09:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** 관리자 근태 보정 모달 — 특정 직원의 특정일 출근/퇴근 시각 수정(또는 신규 기록). */
export function AttendanceCorrectModal(
  { upn, name, date, checkInAt, checkOutAt, onClose, onDone }:
  { upn: string; name: string; date: string; checkInAt?: string | null; checkOutAt?: string | null;
    onClose: () => void; onDone: (msg: string) => void },
): JSX.Element {
  const qc = useQueryClient();
  const [inTime, setInTime] = useState(hhmm(checkInAt));
  const [outTime, setOutTime] = useState(hhmm(checkOutAt));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const outBeforeIn = Boolean(inTime && outTime && outTime < inTime);
  const canSubmit = inTime.length > 0 && !outBeforeIn; // 출근 시각은 필수

  const submit = async (): Promise<void> => {
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      await api.correctAttendance({
        upn, date,
        checkInAt: toIso(date, inTime),
        checkOutAt: toIso(date, outTime),
        note: note.trim() || undefined,
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['attendance', 'admin', date] }),
        qc.invalidateQueries({ queryKey: ['board', date] }),
      ]);
      onDone(`${name}님 ${date} 근태를 보정했습니다`);
      onClose();
    } catch (e) {
      onDone(e instanceof Error ? e.message : '보정 실패');
      setBusy(false);
    }
  };

  return (
    <Modal width={420} onClose={onClose}>
      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>근태 보정</div>
          <button title="닫기" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280', fontSize: 26, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 18 }}>{name} · {date}</div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <label style={field}><span style={lbl}>출근 시각</span>
            <input type="time" className="dx-input" value={inTime} onChange={(e) => setInTime(e.target.value)} />
          </label>
          <label style={field}><span style={lbl}>퇴근 시각</span>
            <input type="time" className="dx-input" value={outTime} onChange={(e) => setOutTime(e.target.value)} />
          </label>
        </div>
        {outBeforeIn && <div style={errStyle}>퇴근 시각이 출근 시각보다 빠릅니다.</div>}

        <label style={{ ...field, marginBottom: 6 }}><span style={lbl}>보정 사유 <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(선택)</span></span>
          <input className="dx-input" placeholder="예: 지문인식 오류로 실제 출근시각 반영" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>

        <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8, lineHeight: 1.6 }}>
          ※ 저장 시 상태가 <b>보정</b>으로 표시됩니다. 퇴근 시각을 비우면 퇴근 미기록(근무 중) 처리됩니다.
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button className="dx-btn dx-btn-ghost" style={{ flex: 1 }} onClick={onClose}>취소</button>
          <button className="dx-btn dx-btn-primary" style={{ flex: 1 }} disabled={busy || !canSubmit} onClick={submit}>{busy ? '저장 중…' : '보정 저장'}</button>
        </div>
      </div>
    </Modal>
  );
}

const field = { flex: 1, display: 'flex', flexDirection: 'column', gap: 6 } as const;
const lbl = { fontSize: 12, fontWeight: 600, color: '#6B7280' } as const;
const errStyle = { background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#B91C1C', borderRadius: 8, padding: '8px 11px', fontSize: 12.5, fontWeight: 600, marginBottom: 14 } as const;
